import { edgeDevicesActionTypes } from './edgeDevicesTypes';

const initialState = {
  // Cluster data
  clusters: [],
  clusterDetails: {}, // Keyed by clusterId
  clusterHealth: {}, // Keyed by clusterId
  serviceStatus: null,
  
  // Node data
  nodes: {},  // Keyed by clusterId
  unassignedNodes: [], // Nodes not assigned to any cluster
  nodeTelemetry: {}, // Keyed by nodeId
  
  // Application data
  applications: {}, // Keyed by clusterId
  applicationDetails: {}, // Keyed by appId
  
  // Services and pods (from cluster details)
  services: {}, // Keyed by clusterId
  pods: {}, // Keyed by clusterId
  
  // Terminal state
  activeCluster: null,
  activeNamespace: 'default',
  terminalConnected: false,
  terminalOutput: [],
  
  // RBAC related states
  rbacUsers: [],
  roles: [],
  
  // Device registration (admin only)
  pendingDevices: [],
  
  // Loading states
  isFetching: false,
  isCreating: false,
  isDeleting: false,
  isUpdating: false,
  isInstalling: false,
  isExecutingCommand: false,
  isLoadingRbac: false,
  isLoadingDevices: false,
  
  // Error states
  errors: null,
  rbacError: null,
  deviceError: null
};

const edgeDevicesReducer = (state = initialState, action) => {
  switch (action.type) {
    // ============================================================================
    // CLUSTER MANAGEMENT CASES
    // ============================================================================
    
    // Fetch clusters
    case edgeDevicesActionTypes.FETCH_CLUSTERS_START:
      return {
        ...state,
        isFetching: true,
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_CLUSTERS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        clusters: action.payload || [], // Handle empty array for new users
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_CLUSTERS_FAILURE:
      return {
        ...state,
        isFetching: false,
        clusters: [], // Reset to empty array on error
        errors: action.payload
      };
    
    // Create cluster
    case edgeDevicesActionTypes.CREATE_CLUSTER_START:
      return {
        ...state,
        isCreating: true,
        errors: null
      };
    case edgeDevicesActionTypes.CREATE_CLUSTER_SUCCESS:
      return {
        ...state,
        isCreating: false,
        clusters: [...state.clusters, action.payload],
        errors: null
      };
    case edgeDevicesActionTypes.CREATE_CLUSTER_FAILURE:
      return {
        ...state,
        isCreating: false,
        errors: action.payload
      };

    // Fetch cluster details
    case edgeDevicesActionTypes.FETCH_CLUSTER_DETAILS_START:
      return {
        ...state,
        isFetching: true,
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_CLUSTER_DETAILS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        clusterDetails: {
          ...state.clusterDetails,
          [action.payload._id]: action.payload
        },
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_CLUSTER_DETAILS_FAILURE:
      return {
        ...state,
        isFetching: false,
        errors: action.payload
      };

    // Fetch cluster health
    case edgeDevicesActionTypes.FETCH_CLUSTER_HEALTH_START:
      return {
        ...state,
        isFetching: true,
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_CLUSTER_HEALTH_SUCCESS:
      return {
        ...state,
        isFetching: false,
        clusterHealth: {
          ...state.clusterHealth,
          [action.payload.clusterId]: action.payload.healthData
        },
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_CLUSTER_HEALTH_FAILURE:
      return {
        ...state,
        isFetching: false,
        errors: action.payload
      };

    // Fetch service status
    case edgeDevicesActionTypes.FETCH_SERVICE_STATUS_START:
      return {
        ...state,
        isFetching: true,
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_SERVICE_STATUS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        serviceStatus: action.payload,
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_SERVICE_STATUS_FAILURE:
      return {
        ...state,
        isFetching: false,
        errors: action.payload
      };
    
    // Delete cluster
    case edgeDevicesActionTypes.DELETE_CLUSTER_START:
      return {
        ...state,
        isDeleting: true,
        errors: null
      };
    case edgeDevicesActionTypes.DELETE_CLUSTER_SUCCESS:
      const deletedClusterId = action.payload;
      const { [deletedClusterId]: deletedCluster, ...remainingClusters } = state.clusterDetails;
      const { [deletedClusterId]: deletedHealth, ...remainingHealth } = state.clusterHealth;
      const { [deletedClusterId]: deletedNodes, ...remainingNodes } = state.nodes;
      const { [deletedClusterId]: deletedApps, ...remainingApps } = state.applications;
      const { [deletedClusterId]: deletedServices, ...remainingServices } = state.services;
      const { [deletedClusterId]: deletedPods, ...remainingPods } = state.pods;
      
      return {
        ...state,
        isDeleting: false,
        clusters: state.clusters.filter(cluster => cluster._id !== deletedClusterId),
        clusterDetails: remainingClusters,
        clusterHealth: remainingHealth,
        nodes: remainingNodes,
        applications: remainingApps,
        services: remainingServices,
        pods: remainingPods,
        activeCluster: state.activeCluster === deletedClusterId ? null : state.activeCluster,
        errors: null
      };
    case edgeDevicesActionTypes.DELETE_CLUSTER_FAILURE:
      return {
        ...state,
        isDeleting: false,
        errors: action.payload
      };
    
    // Update cluster status
    case edgeDevicesActionTypes.UPDATE_CLUSTER_STATUS_START:
      return {
        ...state,
        isUpdating: true,
        errors: null
      };
    case edgeDevicesActionTypes.UPDATE_CLUSTER_STATUS_SUCCESS:
      return {
        ...state,
        isUpdating: false,
        clusters: state.clusters.map(cluster => 
          cluster._id === action.payload.clusterId 
            ? { ...cluster, status: action.payload.status } 
            : cluster
        ),
        errors: null
      };
    case edgeDevicesActionTypes.UPDATE_CLUSTER_STATUS_FAILURE:
      return {
        ...state,
        isUpdating: false,
        errors: action.payload
      };

    // ============================================================================
    // NODE MANAGEMENT CASES
    // ============================================================================
    
    // Fetch nodes
    case edgeDevicesActionTypes.FETCH_NODES_START:
      return {
        ...state,
        isFetching: true,
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_NODES_SUCCESS:
      return {
        ...state,
        isFetching: false,
        nodes: {
          ...state.nodes,
          [action.payload.clusterId]: action.payload.nodes || [] // Handle empty array for new clusters
        },
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_NODES_FAILURE:
      return {
        ...state,
        isFetching: false,
        errors: action.payload
      };

    // Create node manually
    case edgeDevicesActionTypes.CREATE_NODE_START:
      return {
        ...state,
        isCreating: true,
        errors: null
      };
    case edgeDevicesActionTypes.CREATE_NODE_SUCCESS:
      const { clusterId: createClusterId, node: newNode } = action.payload;
      const clusterNodes = state.nodes[createClusterId] || [];
      
      return {
        ...state,
        isCreating: false,
        nodes: {
          ...state.nodes,
          [createClusterId]: [...clusterNodes, newNode]
        },
        errors: null
      };
    case edgeDevicesActionTypes.CREATE_NODE_FAILURE:
      return {
        ...state,
        isCreating: false,
        errors: action.payload
      };

    // Fetch node telemetry
    case edgeDevicesActionTypes.FETCH_NODE_TELEMETRY_START:
      return {
        ...state,
        isFetching: true,
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_NODE_TELEMETRY_SUCCESS:
      return {
        ...state,
        isFetching: false,
        nodeTelemetry: {
          ...state.nodeTelemetry,
          [action.payload.nodeId]: action.payload.telemetryData
        },
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_NODE_TELEMETRY_FAILURE:
      return {
        ...state,
        isFetching: false,
        errors: action.payload
      };
    
    // Update node status
    case edgeDevicesActionTypes.UPDATE_NODE_STATUS_START:
      return {
        ...state,
        isUpdating: true,
        errors: null
      };
    case edgeDevicesActionTypes.UPDATE_NODE_STATUS_SUCCESS:
      const { clusterId: nodeClusterId, nodeId, status } = action.payload;
      const clusterNodesForUpdate = state.nodes[nodeClusterId] || [];
      
      return {
        ...state,
        isUpdating: false,
        nodes: {
          ...state.nodes,
          [nodeClusterId]: clusterNodesForUpdate.map(node => 
            node._id === nodeId 
              ? { ...node, status } 
              : node
          )
        },
        errors: null
      };
    case edgeDevicesActionTypes.UPDATE_NODE_STATUS_FAILURE:
      return {
        ...state,
        isUpdating: false,
        errors: action.payload
      };

    // Delete node
    case edgeDevicesActionTypes.DELETE_NODE_START:
      return {
        ...state,
        isDeleting: true,
        errors: null
      };
    case edgeDevicesActionTypes.DELETE_NODE_SUCCESS:
      const { clusterId: deleteClusterId, nodeId: deleteNodeId } = action.payload;
      const clusterNodesForDelete = state.nodes[deleteClusterId] || [];
      
      return {
        ...state,
        isDeleting: false,
        nodes: {
          ...state.nodes,
          [deleteClusterId]: clusterNodesForDelete.filter(node => node._id !== deleteNodeId)
        },
        errors: null
      };
    case edgeDevicesActionTypes.DELETE_NODE_FAILURE:
      return {
        ...state,
        isDeleting: false,
        errors: action.payload
      };
    
    // Assign node to cluster
    case edgeDevicesActionTypes.ASSIGN_NODE_START:
      return {
        ...state,
        isUpdating: true,
        errors: null
      };
    case edgeDevicesActionTypes.ASSIGN_NODE_SUCCESS:
      const { clusterId: assignClusterId, node: assignedNode } = action.payload;
      const assignClusterNodes = state.nodes[assignClusterId] || [];
      
      return {
        ...state,
        isUpdating: false,
        nodes: {
          ...state.nodes,
          [assignClusterId]: [...assignClusterNodes, assignedNode]
        },
        // Remove from unassigned nodes
        unassignedNodes: state.unassignedNodes.filter(node => node._id !== assignedNode._id),
        errors: null
      };
    case edgeDevicesActionTypes.ASSIGN_NODE_FAILURE:
      return {
        ...state,
        isUpdating: false,
        errors: action.payload
      };

    // Remove node from cluster
    case edgeDevicesActionTypes.REMOVE_NODE_START:
      return {
        ...state,
        isUpdating: true,
        errors: null
      };
    case edgeDevicesActionTypes.REMOVE_NODE_SUCCESS:
      const { clusterId: removeClusterId, nodeId: removeNodeId, node: removedNode } = action.payload;
      const removeClusterNodes = state.nodes[removeClusterId] || [];
      
      return {
        ...state,
        isUpdating: false,
        nodes: {
          ...state.nodes,
          [removeClusterId]: removeClusterNodes.filter(node => node._id !== removeNodeId)
        },
        // Add to unassigned nodes
        unassignedNodes: [...state.unassignedNodes, removedNode],
        errors: null
      };
    case edgeDevicesActionTypes.REMOVE_NODE_FAILURE:
      return {
        ...state,
        isUpdating: false,
        errors: action.payload
      };

    // Transfer node between clusters
    case edgeDevicesActionTypes.TRANSFER_NODE_START:
      return {
        ...state,
        isUpdating: true,
        errors: null
      };
    case edgeDevicesActionTypes.TRANSFER_NODE_SUCCESS:
      const { sourceClusterId, targetClusterId, node: transferredNode } = action.payload;
      const sourceNodes = state.nodes[sourceClusterId] || [];
      const targetNodes = state.nodes[targetClusterId] || [];
      
      return {
        ...state,
        isUpdating: false,
        nodes: {
          ...state.nodes,
          [sourceClusterId]: sourceNodes.filter(node => node._id !== transferredNode._id),
          [targetClusterId]: [...targetNodes, { ...transferredNode, clusterId: targetClusterId }]
        },
        errors: null
      };
    case edgeDevicesActionTypes.TRANSFER_NODE_FAILURE:
      return {
        ...state,
        isUpdating: false,
        errors: action.payload
      };

    // Fetch unassigned nodes
    case edgeDevicesActionTypes.FETCH_UNASSIGNED_NODES_START:
      return {
        ...state,
        isFetching: true,
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_UNASSIGNED_NODES_SUCCESS:
      return {
        ...state,
        isFetching: false,
        unassignedNodes: action.payload || [], // Handle empty array for new users
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_UNASSIGNED_NODES_FAILURE:
      return {
        ...state,
        isFetching: false,
        unassignedNodes: [], // Reset to empty array on error
        errors: action.payload
      };

    // ============================================================================
    // APPLICATION MANAGEMENT CASES
    // ============================================================================

    // Fetch applications
    case edgeDevicesActionTypes.FETCH_APPS_START:
      return {
        ...state,
        isFetching: true,
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_APPS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        applications: {
          ...state.applications,
          [action.payload.clusterId]: action.payload.apps || [] // Handle empty array for new clusters
        },
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_APPS_FAILURE:
      return {
        ...state,
        isFetching: false,
        errors: action.payload
      };
    
    // Install application
    case edgeDevicesActionTypes.INSTALL_APP_START:
      return {
        ...state,
        isInstalling: true,
        errors: null
      };
    case edgeDevicesActionTypes.INSTALL_APP_SUCCESS:
      const { clusterId: installClusterId, appDetails } = action.payload;
      const clusterApps = state.applications[installClusterId] || [];
      
      return {
        ...state,
        isInstalling: false,
        applications: {
          ...state.applications,
          [installClusterId]: [...clusterApps, appDetails]
        },
        errors: null
      };
    case edgeDevicesActionTypes.INSTALL_APP_FAILURE:
      return {
        ...state,
        isInstalling: false,
        errors: action.payload
      };

    // Fetch application details
    case edgeDevicesActionTypes.FETCH_APP_DETAILS_START:
      return {
        ...state,
        isFetching: true,
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_APP_DETAILS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        applicationDetails: {
          ...state.applicationDetails,
          [action.payload.appId]: action.payload.appDetails
        },
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_APP_DETAILS_FAILURE:
      return {
        ...state,
        isFetching: false,
        errors: action.payload
      };

    // Update application status
    case edgeDevicesActionTypes.UPDATE_APP_STATUS_START:
      return {
        ...state,
        isUpdating: true,
        errors: null
      };
    case edgeDevicesActionTypes.UPDATE_APP_STATUS_SUCCESS:
      const { clusterId: appClusterId, appId: updateAppId, status: appStatus } = action.payload;
      const appsForUpdate = state.applications[appClusterId] || [];
      
      return {
        ...state,
        isUpdating: false,
        applications: {
          ...state.applications,
          [appClusterId]: appsForUpdate.map(app => 
            app._id === updateAppId 
              ? { ...app, status: appStatus } 
              : app
          )
        },
        errors: null
      };
    case edgeDevicesActionTypes.UPDATE_APP_STATUS_FAILURE:
      return {
        ...state,
        isUpdating: false,
        errors: action.payload
      };

    // Delete application
    case edgeDevicesActionTypes.DELETE_APP_START:
      return {
        ...state,
        isDeleting: true,
        errors: null
      };
    case edgeDevicesActionTypes.DELETE_APP_SUCCESS:
      const { clusterId: deleteAppClusterId, appId: deleteAppId } = action.payload;
      const appsForDelete = state.applications[deleteAppClusterId] || [];
      
      return {
        ...state,
        isDeleting: false,
        applications: {
          ...state.applications,
          [deleteAppClusterId]: appsForDelete.filter(app => app._id !== deleteAppId)
        },
        errors: null
      };
    case edgeDevicesActionTypes.DELETE_APP_FAILURE:
      return {
        ...state,
        isDeleting: false,
        errors: action.payload
      };

    // Scale application
    case edgeDevicesActionTypes.SCALE_APP_START:
      return {
        ...state,
        isUpdating: true,
        errors: null
      };
    case edgeDevicesActionTypes.SCALE_APP_SUCCESS:
      const { clusterId: scaleClusterId, appId: scaleAppId, replicas } = action.payload;
      const appsForScale = state.applications[scaleClusterId] || [];
      
      return {
        ...state,
        isUpdating: false,
        applications: {
          ...state.applications,
          [scaleClusterId]: appsForScale.map(app => 
            app._id === scaleAppId 
              ? { ...app, replicas } 
              : app
          )
        },
        errors: null
      };
    case edgeDevicesActionTypes.SCALE_APP_FAILURE:
      return {
        ...state,
        isUpdating: false,
        errors: action.payload
      };

    // Restart application
    case edgeDevicesActionTypes.RESTART_APP_START:
      return {
        ...state,
        isUpdating: true,
        errors: null
      };
    case edgeDevicesActionTypes.RESTART_APP_SUCCESS:
      // For restart, we might want to update the lastRestarted timestamp
      // This would depend on the API response structure
      return {
        ...state,
        isUpdating: false,
        errors: null
      };
    case edgeDevicesActionTypes.RESTART_APP_FAILURE:
      return {
        ...state,
        isUpdating: false,
        errors: action.payload
      };

    // ============================================================================
    // SERVICES AND PODS CASES
    // ============================================================================
    
    // Fetch services
    case edgeDevicesActionTypes.FETCH_SERVICES_START:
      return {
        ...state,
        isFetching: true,
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_SERVICES_SUCCESS:
      return {
        ...state,
        isFetching: false,
        services: {
          ...state.services,
          [action.payload.clusterId]: action.payload.services || []
        },
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_SERVICES_FAILURE:
      return {
        ...state,
        isFetching: false,
        errors: action.payload
      };
    
    // Fetch pods
    case edgeDevicesActionTypes.FETCH_PODS_START:
      return {
        ...state,
        isFetching: true,
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_PODS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        pods: {
          ...state.pods,
          [action.payload.clusterId]: action.payload.pods || []
        },
        errors: null
      };
    case edgeDevicesActionTypes.FETCH_PODS_FAILURE:
      return {
        ...state,
        isFetching: false,
        errors: action.payload
      };

    // ============================================================================
    // DEVICE REGISTRATION MANAGEMENT CASES (Admin Only)
    // ============================================================================

    // Fetch pending devices
    case edgeDevicesActionTypes.FETCH_PENDING_DEVICES_START:
      return {
        ...state,
        isLoadingDevices: true,
        deviceError: null
      };
    case edgeDevicesActionTypes.FETCH_PENDING_DEVICES_SUCCESS:
      return {
        ...state,
        isLoadingDevices: false,
        pendingDevices: action.payload || [], // Handle empty array
        deviceError: null
      };
    case edgeDevicesActionTypes.FETCH_PENDING_DEVICES_FAILURE:
      return {
        ...state,
        isLoadingDevices: false,
        pendingDevices: [], // Reset to empty array on error
        deviceError: action.payload
      };

    // Approve device registration
    case edgeDevicesActionTypes.APPROVE_DEVICE_START:
      return {
        ...state,
        isUpdating: true,
        deviceError: null
      };
    case edgeDevicesActionTypes.APPROVE_DEVICE_SUCCESS:
      return {
        ...state,
        isUpdating: false,
        // Remove from pending devices
        pendingDevices: state.pendingDevices.filter(device => device._id !== action.payload._id),
        deviceError: null
      };
    case edgeDevicesActionTypes.APPROVE_DEVICE_FAILURE:
      return {
        ...state,
        isUpdating: false,
        deviceError: action.payload
      };

    // Reject device registration
    case edgeDevicesActionTypes.REJECT_DEVICE_START:
      return {
        ...state,
        isDeleting: true,
        deviceError: null
      };
    case edgeDevicesActionTypes.REJECT_DEVICE_SUCCESS:
      return {
        ...state,
        isDeleting: false,
        // Remove from pending devices
        pendingDevices: state.pendingDevices.filter(device => device._id !== action.payload),
        deviceError: null
      };
    case edgeDevicesActionTypes.REJECT_DEVICE_FAILURE:
      return {
        ...state,
        isDeleting: false,
        deviceError: action.payload
      };

    // ============================================================================
    // TERMINAL CASES
    // ============================================================================
    
    // Terminal command operations
    case edgeDevicesActionTypes.EXECUTE_TERMINAL_COMMAND_START:
      return {
        ...state,
        isExecutingCommand: true,
        errors: null
      };
    case edgeDevicesActionTypes.EXECUTE_TERMINAL_COMMAND_SUCCESS:
      return {
        ...state,
        isExecutingCommand: false,
        errors: null
      };
    case edgeDevicesActionTypes.EXECUTE_TERMINAL_COMMAND_FAILURE:
      return {
        ...state,
        isExecutingCommand: false,
        errors: action.payload
      };
    
    // Set active cluster (for UI state)
    case edgeDevicesActionTypes.SET_ACTIVE_CLUSTER:
      return {
        ...state,
        activeCluster: action.payload
      };

    case edgeDevicesActionTypes.SET_ACTIVE_NAMESPACE:
      return {
        ...state,
        activeNamespace: action.payload
      };
    
    // Terminal connection
    case edgeDevicesActionTypes.CONNECT_TERMINAL_START:
      return {
        ...state,
        isFetching: true,
        errors: null
      };
    case edgeDevicesActionTypes.CONNECT_TERMINAL_SUCCESS:
      return {
        ...state,
        isFetching: false,
        terminalConnected: true,
        activeCluster: action.payload,
        errors: null
      };
    case edgeDevicesActionTypes.CONNECT_TERMINAL_FAILURE:
      return {
        ...state,
        isFetching: false,
        terminalConnected: false,
        errors: action.payload
      };
    case edgeDevicesActionTypes.DISCONNECT_TERMINAL:
      return {
        ...state,
        terminalConnected: false,
        terminalOutput: []
      };
    
    // Terminal output
    case edgeDevicesActionTypes.CLEAR_TERMINAL_OUTPUT:
      return {
        ...state,
        terminalOutput: []
      };
    case edgeDevicesActionTypes.ADD_TERMINAL_OUTPUT:
      return {
        ...state,
        terminalOutput: [...state.terminalOutput, action.payload]
      };

    // ============================================================================
    // RBAC CASES
    // ============================================================================
    
    // RBAC - Users
    case edgeDevicesActionTypes.FETCH_RBAC_USERS_START:
      return {
        ...state,
        isLoadingRbac: true,
        rbacError: null
      };
    case edgeDevicesActionTypes.FETCH_RBAC_USERS_SUCCESS:
      return {
        ...state,
        isLoadingRbac: false,
        rbacUsers: action.payload || [], // Handle empty array
        rbacError: null
      };
    case edgeDevicesActionTypes.FETCH_RBAC_USERS_FAILURE:
      return {
        ...state,
        isLoadingRbac: false,
        rbacUsers: [], // Reset to empty array on error
        rbacError: action.payload
      };
    
    // RBAC - Invite User
    case edgeDevicesActionTypes.INVITE_USER_START:
      return {
        ...state,
        isLoadingRbac: true,
        rbacError: null
      };
    case edgeDevicesActionTypes.INVITE_USER_SUCCESS:
      return {
        ...state,
        isLoadingRbac: false,
        rbacUsers: [...state.rbacUsers, action.payload],
        rbacError: null
      };
    case edgeDevicesActionTypes.INVITE_USER_FAILURE:
      return {
        ...state,
        isLoadingRbac: false,
        rbacError: action.payload
      };
    
    // RBAC - Update User Role
    case edgeDevicesActionTypes.UPDATE_USER_ROLE_START:
      return {
        ...state,
        isLoadingRbac: true,
        rbacError: null
      };
    case edgeDevicesActionTypes.UPDATE_USER_ROLE_SUCCESS:
      return {
        ...state,
        isLoadingRbac: false,
        rbacUsers: state.rbacUsers.map(user => 
          user._id === action.payload._id 
            ? action.payload 
            : user
        ),
        rbacError: null
      };
    case edgeDevicesActionTypes.UPDATE_USER_ROLE_FAILURE:
      return {
        ...state,
        isLoadingRbac: false,
        rbacError: action.payload
      };
    
    // RBAC - Delete User
    case edgeDevicesActionTypes.DELETE_USER_START:
      return {
        ...state,
        isLoadingRbac: true,
        rbacError: null
      };
    case edgeDevicesActionTypes.DELETE_USER_SUCCESS:
      return {
        ...state,
        isLoadingRbac: false,
        rbacUsers: state.rbacUsers.filter(user => user._id !== action.payload),
        rbacError: null
      };
    case edgeDevicesActionTypes.DELETE_USER_FAILURE:
      return {
        ...state,
        isLoadingRbac: false,
        rbacError: action.payload
      };
    
    // RBAC - Fetch Roles
    case edgeDevicesActionTypes.FETCH_ROLES_START:
      return {
        ...state,
        isLoadingRbac: true,
        rbacError: null
      };
    case edgeDevicesActionTypes.FETCH_ROLES_SUCCESS:
      return {
        ...state,
        isLoadingRbac: false,
        roles: action.payload?.roles || [], // Handle nested roles structure
        rbacError: null
      };
    case edgeDevicesActionTypes.FETCH_ROLES_FAILURE:
      return {
        ...state,
        isLoadingRbac: false,
        roles: [], // Reset to empty array on error
        rbacError: action.payload
      };

    default:
      return state;
  }
};

export default edgeDevicesReducer; 